/** "Insert saved reply": pick one of this title's (or the all-titles) saved replies into the Reply body. */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { useInsertIntoBody } from '../replyInsert'
import { TITLE_LABELS, type SavedReply } from '../../lib/supportDesk'
import { filterSavedReplies } from '../../lib/supportWorkspace'

export function SavedReplyPicker({ publication }: { publication: string }) {
  const { ready, reason, insertIntoBody } = useInsertIntoBody()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const replies = useQuery<SavedReply[]>({
    queryKey: ['support', 'saved-replies', publication],
    queryFn: () => api.get<SavedReply[]>(`/api/support/saved-replies?publication=${encodeURIComponent(publication)}`),
    enabled: open, staleTime: 60_000, retry: false,
  })
  const shown = filterSavedReplies(replies.data ?? [], q)
  const choose = (r: SavedReply) => { insertIntoBody(r.body_md); setOpen(false); setQ('') }

  return (
    <span className="relative inline-block">
      <button type="button" className="label hover:underline disabled:opacity-50" disabled={!ready}
        title={ready ? undefined : reason} aria-expanded={open} onClick={() => setOpen(o => !o)}>
        Insert saved reply
      </button>
      {open && ready && (
        <div role="dialog" aria-label="Saved replies"
          className="absolute z-20 left-0 bottom-full mb-2 w-[360px] max-w-[90vw] bg-ink-800 border border-ink-500 shadow-panel p-2 space-y-2">
          <input aria-label="Filter saved replies" placeholder="Filter by name or text" value={q} autoFocus
            onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
            className="w-full bg-ink-900 border border-ink-500 px-2 py-1 text-paper font-mono text-[12px]" />
          {replies.isLoading && <p className="text-paper-trace text-sm">Loading…</p>}
          {replies.isError && <p className="text-red-400 text-sm">Couldn't load saved replies.</p>}
          {replies.data && shown.length === 0 && (
            <p className="text-paper-trace text-sm">{q.trim() ? 'No saved reply matches.' : 'No saved replies yet.'}</p>
          )}
          <ul className="max-h-[280px] overflow-y-auto">
            {shown.map(r => (
              <li key={r.id}>
                <button type="button" onClick={() => choose(r)} className="w-full text-left px-2 py-2 hover:bg-ink-700">
                  <span className="text-paper text-sm">{r.name}</span>
                  <span className="label ml-2">{r.publication ? TITLE_LABELS[r.publication] ?? r.publication : 'All titles'}</span>
                  <span className="block text-paper-trace text-xs truncate">{r.body_md}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  )
}
